import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHits } from 'react-instantsearch';

import { SearchResults } from '@uncinc/react-searchkit';

import SearchAllContent from '../../SearchAllContent';
import { byThemeRequestOptions, hitsPerPage, searchSettings } from '../config';
import createTopicalUrl from '../createTopicalUrl';
import renderRelatedHit from '../renderHit';
import RelatedContent from '..';

interface RelatedContentByThemeProps {
  contentType?: 'article' | 'event';
  themeIds: number[];
  themeName?: string;
  excludeNid?: number;
  title?: string;
  className?: string;
}

const RelatedContentByThemeInner: React.FC<Pick<RelatedContentByThemeProps, 'contentType' | 'themeName' | 'title' | 'className'>> = ({
  contentType,
  themeName,
  title,
  className,
}) => {
  const { hits } = useHits();
  const { t } = useTranslation();

  if (!hits.length) {
    return null;
  }

  const moreUrl = createTopicalUrl(contentType, 'theme', themeName);

  return (
    <RelatedContent className={className}>
      <RelatedContent.Title title={title ?? t('components.related_content.title')} />
      <SearchResults className="RelatedContent__items" renderItem={renderRelatedHit} />
      <RelatedContent.MoreButton url={moreUrl} />
    </RelatedContent>
  );
};

const RelatedContentByTheme: React.FC<RelatedContentByThemeProps> = ({
  contentType,
  themeIds,
  themeName,
  excludeNid,
  title,
  className,
}) => {
  const requestOptions = useMemo(
    () => byThemeRequestOptions(themeIds, excludeNid),
    [themeIds, excludeNid],
  );

  return (
    <SearchAllContent
      hitsPerPage={hitsPerPage}
      searchSettings={searchSettings}
      requestOptions={requestOptions}
    >
      <RelatedContentByThemeInner
        contentType={contentType}
        themeName={themeName}
        className={className}
        title={title}
      />
    </SearchAllContent>
  );
};

export default RelatedContentByTheme;
